interface ListItem extends Recordable {
  id: number
  parentId: number
}

interface TreeObj extends ListItem {
  children: TreeObj[] | null
}

interface TreeHelperConfig {
  id: string
  children: string
  pid: string
}

const DEFAULT_CONFIG: TreeHelperConfig = {
  id: 'id',
  children: 'children',
  pid: 'pid',
}

const getConfig = (config: Partial<TreeHelperConfig>) => Object.assign({}, DEFAULT_CONFIG, config)

/**
 * 列表转树状结构
 * @param list
 * @param config 字段配置
 * @returns
 */
export function listToTree<T = any>(list: any[], config: Partial<TreeHelperConfig> = {}): T[] {
  if (list.length === 0) {
    return []
  }
  const conf = getConfig(config)
  const { id, children, pid } = conf

  const idMap = list.reduce((map, cur) => ((map[cur[id]] = cur), map), {})
  const result: T[] = []

  for (const item of list) {
    const parent = idMap[item[pid]]
    if (parent) {
      parent[children] = parent[children] || []
      parent[children].push(item)
    } else {
      result.push(item)
    }
  }

  return result
}

/**
 * 树状结构扁平化
 * @param tree
 * @param config 字段配置
 * @param reserveChildren 是否保留children字段
 * @returns
 */
export function treeToList<T extends Recordable>(
  tree: any[],
  config: Partial<TreeHelperConfig> = {},
  reserveChildren = false,
): T[] {
  if (tree.length === 0) {
    return []
  }
  const conf = getConfig(config)
  const { children } = conf
  const result: T[] = [...tree]
  for (let i = 0; i < result.length; i++) {
    const tmpObj = result[i]
    const innerChildren = tmpObj[children]
    if (!innerChildren || !innerChildren.length) continue
    if (reserveChildren === false) {
      Reflect.deleteProperty(tmpObj, children)
    }
    result.splice(i + 1, 0, ...innerChildren)
  }
  return result
}

/**
 * 更新所有层级子级元素状态
 * @param children 子元素列表
 * @param checked 状态
 * @returns
 */
export function updateChildren(children: TreeObj[], checked: boolean) {
  if (children.length === 0) {
    return
  }
  children.forEach((child) => {
    child.checked = checked
    if (child.children && child.children.length) {
      updateChildren(child.children, checked)
    }
  })
}

/**
 * 更新所有层级父级元素状态
 * @param treeList 扁平树列表
 * @param treeObj 树节点
 * @param checked 状态
 * @returns
 */
export function updateParent(treeList: TreeObj[], treeObj: TreeObj, checked: boolean) {
  const parent = treeList.find((item) => item.id === treeObj.parentId)
  if (!parent) {
    return
  }
  if (checked) {
    parent.checked = checked
  } else {
    const children = treeList.filter((item) => item.parentId === treeObj.parentId)
    parent.checked = children.some((item) => item.checked)
  }

  if (parent.parentId > 0) {
    updateParent(treeList, parent, checked)
  }
}

/**
 * 对象添加额外属性
 * @param { 对象或对象数组 } source
 * @param { 对象或对象数组，对象属性(key, value) } properties
 */
export const addProperties = <T extends Recordable>(
  source: Recordable | Recordable[],
  properties: Recordable | Recordable[] = [],
) => {
  source = (Array.isArray(source) && source) || [source]
  properties = (Array.isArray(properties) && properties) || [properties]
  try {
    properties.forEach(({ key, value }) => {
      source.forEach((item: Recordable) => {
        const v = typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value
        item[key] = v
        if (item.children && item.children.length > 0) {
          addProperties(item.children, properties)
        }
      })
    })
  } catch (e) {
    console.error('给对象添加额外属性失败!')
  }
  return source as T[]
}

/**
 * 在树中查找单个节点
 * @param tree
 * @param func
 * @param config
 * @returns
 */
export function findNode<T = any>(
  tree: any,
  func: Fn,
  config: Partial<TreeHelperConfig> = {},
): T | null {
  config = getConfig(config)
  const { children } = config
  const list = [...tree]
  for (const node of list) {
    if (func(node)) {
      return node
    }
    node[children!] && list.push(...node[children!])
  }
  return null
}

/**
 * 在树中查找单个所有符合要求的节点
 * @param tree
 * @param func
 * @param config
 * @returns
 */
export function findNodeAll<T = any>(
  tree: any,
  func: Fn,
  config: Partial<TreeHelperConfig> = {},
): T[] {
  config = getConfig(config)
  const { children } = config
  const list = [...tree]
  const result: T[] = []
  for (const node of list) {
    func(node) && result.push(node)
    node[children!] && list.push(...node[children!])
  }
  return result
}

/**
 * 在树中查找符合要求的节点的路径
 * @param tree
 * @param func
 * @param config
 * @returns
 */
export function findPath<T = any>(
  tree: any[],
  func: Fn,
  config: Partial<TreeHelperConfig> = {},
): T | T[] | null {
  config = getConfig(config)
  const path: T[] = []
  const list = [...tree]
  const visitedSet = new Set()
  const { children } = config
  while (list.length) {
    const node = list[0]
    if (visitedSet.has(node)) {
      path.pop()
      list.shift()
    } else {
      visitedSet.add(node)
      node[children!] && list.unshift(...node[children!])
      path.push(node)
      if (func(node)) {
        return path
      }
    }
  }
  return null
}

export function findPathAll(tree: any[], func: Fn, config: Partial<TreeHelperConfig> = {}) {
  config = getConfig(config)
  const path: any[] = []
  const list = [...tree]
  const result: any[] = []
  const visitedSet = new Set()
  const { children } = config
  while (list.length) {
    const node = list[0]
    if (visitedSet.has(node)) {
      path.pop()
      list.shift()
    } else {
      visitedSet.add(node)
      node[children!] && list.unshift(...node[children!])
      path.push(node)
      func(node) && result.push([...path])
    }
  }
  return result
}

export function filter<T = any>(
  tree: T[],
  func: (n: T) => boolean,
  config: Partial<TreeHelperConfig> = {},
): T[] {
  config = getConfig(config)
  const children = config.children as string
  function listFilter(list: T[]) {
    return list
      .map((node: any) => ({ ...node }))
      .filter((node) => {
        node[children] = node[children] && listFilter(node[children])
        return func(node) || (node[children] && node[children].length)
      })
  }
  return listFilter(tree)
}

export function forEach<T = any>(
  tree: T[],
  func: (n: T) => any,
  config: Partial<TreeHelperConfig> = {},
): void {
  config = getConfig(config)
  const list: any[] = [...tree]
  const { children } = config
  for (let i = 0; i < list.length; i++) {
    if (func(list[i])) {
      return
    }
    children && list[i][children] && list.splice(i + 1, 0, ...list[i][children])
  }
}

export function treeMap<T = any>(treeData: T[], opt: { children?: string; conversion: Fn }): T[] {
  return treeData.map((item) => treeMapEach(item, opt))
}

export function treeMapEach(
  data: any,
  { children = 'children', conversion }: { children?: string; conversion: Fn },
) {
  const haveChildren = Array.isArray(data[children]) && data[children].length > 0
  const conversionData = conversion(data) || {}
  if (haveChildren) {
    return {
      ...conversionData,
      [children]: data[children].map((i: number) =>
        treeMapEach(i, {
          children,
          conversion,
        }),
      ),
    }
  } else {
    return {
      ...conversionData,
    }
  }
}

export default {
  listToTree,
  treeToList,
  updateChildren,
  updateParent,
  addProperties,
}
